import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { createClient } from "@supabase/supabase-js";

export type InstantGameSummary = {
  slug: string;
  name: string;
  image_url: string | null;
  top_prize: string | null;
  price_range: string | null;
  affiliate_link: string | null;
  categories: string[];
  release_date: string | null;
};

export type InstantGameFull = InstantGameSummary & {
  game_content: string | null;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const listInstantGames = createServerFn({ method: "GET" })
  .inputValidator((data: { stateSlug: string }) => data)
  .handler(async ({ data }): Promise<InstantGameSummary[]> => {
    const { cached } = await import("@/lib/server-cache");
    return cached(
      `instant:list:${data.stateSlug}`,
      5 * 60_000,
      async () => {
        const sb = publicClient();
        const { data: rows, error } = await sb
          .from("instant_games")
          .select("slug,name,image_url,top_prize,price_range,affiliate_link,categories,release_date")
          .eq("state_slug", data.stateSlug)
          .order("release_date", { ascending: false })
          .order("created_at", { ascending: false });
        if (error) throw new Error(error.message);
        return (rows ?? []) as InstantGameSummary[];
      },
      { staleWhileRevalidateMs: 30 * 60_000 },
    ).catch((err) => {
      console.error("listInstantGames error", err);
      return [] as InstantGameSummary[];
    });
  });

export type InstantGameSitemapRow = {
  state_slug: string;
  slug: string;
  updated_at: string | null;
};

export const listAllInstantGameSlugs = createServerFn({ method: "GET" })
  .handler(async (): Promise<InstantGameSitemapRow[]> => {
    const { cached } = await import("@/lib/server-cache");
    return cached(
      "instant:all-slugs",
      10 * 60_000,
      async () => {
        const sb = publicClient();
        const { data: rows, error } = await sb
          .from("instant_games")
          .select("state_slug,slug,updated_at")
          .order("state_slug");
        if (error) throw new Error(error.message);
        return (rows ?? []) as InstantGameSitemapRow[];
      },
      { staleWhileRevalidateMs: 60 * 60_000 },
    ).catch((err) => {
      console.error("listAllInstantGameSlugs error", err);
      return [] as InstantGameSitemapRow[];
    });
  });

export const getInstantGame = createServerFn({ method: "GET" })
  .inputValidator((data: { stateSlug: string; slug: string }) => data)
  .handler(async ({ data }): Promise<InstantGameFull | null> => {
    const { cached } = await import("@/lib/server-cache");
    return cached(
      `instant:game:${data.stateSlug}:${data.slug}`,
      5 * 60_000,
      async () => {
        const sb = publicClient();
        const { data: row, error } = await sb
          .from("instant_games")
          .select("slug,name,image_url,top_prize,price_range,affiliate_link,categories,game_content,release_date")
          .eq("state_slug", data.stateSlug)
          .eq("slug", data.slug)
          .maybeSingle();
        if (error) throw new Error(error.message);
        return (row as InstantGameFull) ?? null;
      },
      { staleWhileRevalidateMs: 30 * 60_000 },
    ).catch((err) => {
      console.error("getInstantGame error", err);
      return null;
    });
  });

export function instantGamesListQO(stateSlug: string) {
  return queryOptions({
    queryKey: ["instant-games", stateSlug],
    queryFn: () => listInstantGames({ data: { stateSlug } }),
    staleTime: 5 * 60_000,
  });
}

export function instantGameQO(stateSlug: string, slug: string) {
  return queryOptions({
    queryKey: ["instant-game", stateSlug, slug],
    queryFn: () => getInstantGame({ data: { stateSlug, slug } }),
    staleTime: 5 * 60_000,
  });
}